import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, CheckCircle, User, Bot } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { EscalationBadge, EscalationTrigger } from './EscalationBadge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-utils';
import { humanizeEscalationReason } from '@/lib/escalation-reason';

interface EscalationContext {
  trigger: EscalationTrigger;
  reason: string;
  customer_message?: string;
  recent_messages?: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
  }>;
  error_details?: {
    error_message?: string;
    frustration_score?: number;
    indicators?: string[];
  };
  escalated_at?: string; // Backend uses 'escalated_at', not 'timestamp'
}

interface EscalationContextPanelProps {
  conversationId: string;
  tenantSchema: string;
  escalationContext: EscalationContext;
  onResolved?: () => void;
}

export function EscalationContextPanel({
  conversationId,
  tenantSchema,
  escalationContext,
  onResolved
}: EscalationContextPanelProps) {
  const { t } = useTranslation('conversations');
  const { toast } = useToast();
  const [isResolving, setIsResolving] = useState(false);

  const handleMarkResolved = async () => {
    setIsResolving(true);
    try {
      // Call resolve-escalation Edge Function (bypasses PostgREST PGRST202 cache issues)
      // Background: PostgREST schema cache refuses to refresh, causing PGRST202 errors
      // Solution: Direct PostgreSQL connection via Edge Function bypasses PostgREST entirely
      const { data, error } = await supabase.functions.invoke('resolve-escalation', {
        body: {
          tenant_schema: tenantSchema,
          conversation_id: conversationId
        }
      });

      // Check for Edge Function invocation errors
      if (error) {
        console.error('Edge Function invocation error:', error);
        throw new Error(getErrorMessage(error) || 'Failed to invoke resolve-escalation function');
      }

      // Check for application-level errors from Edge Function
      if (!data?.success) {
        console.error('Edge Function application error:', data?.error || data?.details);
        throw new Error(data?.error || data?.details || 'Failed to resolve escalation');
      }

      console.log('✅ Escalation resolved successfully:', data);

      toast({
        title: t('escalation.toast.resolved'),
        description: t('escalation.toast.resolvedDesc')
      });

      // Notify parent component
      if (onResolved) {
        onResolved();
      }
    } catch (error: unknown) {
      console.error('Failed to resolve escalation:', error);
      toast({
        title: t('escalation.toast.error'),
        description: getErrorMessage(error) || t('escalation.toast.errorDesc'),
        variant: 'destructive'
      });
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/30 dark:bg-orange-900/10">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-base">{t('escalation.panel.title')}</CardTitle>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleMarkResolved}
            disabled={isResolving}
            className="border-green-200 bg-green-50 hover:bg-green-100 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:hover:bg-green-900/30 dark:text-green-400"
          >
            {isResolving ? (
              <>{t('escalation.panel.resolving')}</>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-1" />
                {t('escalation.panel.markResolved')}
              </>
            )}
          </Button>
        </div>
        <CardDescription className="flex items-center gap-2 mt-2">
          <EscalationBadge trigger={escalationContext.trigger} />
          <span className="text-xs text-muted-foreground">
            {escalationContext.escalated_at ? (
              t('escalation.panel.escalatedAgo', {
                time: formatDistanceToNow(new Date(escalationContext.escalated_at), { addSuffix: true })
              })
            ) : (
              t('escalation.panel.recently')
            )}
          </span>
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Trigger & Reason */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">{t('escalation.panel.trigger')}:</span>
              <div className="mt-1">
                <EscalationBadge trigger={escalationContext.trigger} />
              </div>
            </div>
          </div>
          <div>
            <span className="text-muted-foreground text-sm">{t('escalation.panel.reason')}:</span>
            <p className="mt-1 text-sm bg-muted p-2 rounded border">
              {humanizeEscalationReason(escalationContext.reason)}
            </p>
          </div>
        </div>

        <Separator />

        {/* Customer Message */}
        {escalationContext.customer_message && (
          <>
            <div>
              <h4 className="text-sm font-semibold mb-2">{t('escalation.panel.customerQuestion')}</h4>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm whitespace-pre-wrap">{escalationContext.customer_message}</p>
              </div>
            </div>
            <Separator />
          </>
        )}

        {/* Error Details */}
        {escalationContext.error_details && (
          <>
            <div>
              <h4 className="text-sm font-semibold mb-2">
                {escalationContext.error_details.error_message
                  ? t('escalation.panel.errorDetails')
                  : t('escalation.panel.serviceIssue')
                }
              </h4>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-2">
                {escalationContext.error_details.error_message && (
                  <p className="text-sm text-red-900 dark:text-red-200">
                    {escalationContext.error_details.error_message}
                  </p>
                )}
                {escalationContext.error_details.frustration_score !== undefined && (
                  <div className="text-xs text-red-700 dark:text-red-300">
                    Frustration Score: {(escalationContext.error_details.frustration_score * 100).toFixed(0)}%
                  </div>
                )}
                {escalationContext.error_details.indicators && escalationContext.error_details.indicators.length > 0 && (
                  <div className="text-xs text-red-700 dark:text-red-300">
                    Indicators: {escalationContext.error_details.indicators.join(', ')}
                  </div>
                )}
              </div>
            </div>
            <Separator />
          </>
        )}

        {/* Recent Messages */}
        {escalationContext.recent_messages && escalationContext.recent_messages.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold mb-2">{t('escalation.panel.recentMessages')}</h4>
            <ScrollArea className="h-48 w-full border rounded-lg bg-background">
              <div className="p-3 space-y-2">
                {escalationContext.recent_messages.map((message, index) => (
                  <div
                    key={index}
                    className={`p-2 rounded text-sm border-l-4 ${
                      message.role === 'user'
                        ? 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/20'
                        : 'border-l-green-500 bg-green-50/50 dark:bg-green-900/20'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {message.role === 'user' ? (
                        <>
                          <User className="h-3 w-3 text-blue-600" />
                          <span className="text-xs font-medium">{t('escalation.panel.customer')}</span>
                        </>
                      ) : (
                        <>
                          <Bot className="h-3 w-3 text-green-600" />
                          <span className="text-xs font-medium">{t('escalation.panel.agent')}</span>
                        </>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
